import { useEffect, useState } from "react";
import { Eye, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, fmtBR, formatApiError } from "@/lib/api";
import { StatusBadge, fmtCPF } from "@/pages/Students";

const STATUS_LABELS = { ativo: "Ativo", inativo: "Inativo" };
const RELATIONSHIP_LABELS = { responsavel: "Responsável", pai: "Pai", mae: "Mãe", avo: "Avô/Avó", tutor: "Tutor", outro: "Outro" };

const EMPTY = {
  full_name: "", whatsapp: "", email: "", cpf: "",
  birth_date: "", address: "", notes: "", status: "ativo",
};

export default function Guardians() {
  const [guardians, setGuardians] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (statusFilter !== "todos") params.status = statusFilter;
    const { data } = await api.get("/guardians", { params });
    setGuardians(data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openDialog = (guardian) => {
    setEditing(guardian || null);
    setForm(
      guardian
        ? {
            full_name: guardian.full_name, whatsapp: guardian.whatsapp, email: guardian.email,
            cpf: guardian.cpf || "", birth_date: guardian.birth_date || "",
            address: guardian.address || "", notes: guardian.notes || "", status: guardian.status,
          }
        : EMPTY
    );
    setError("");
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      ["cpf", "birth_date", "address", "notes"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      if (editing) await api.put(`/guardians/${editing.id}`, payload);
      else await api.post("/guardians", payload);
      toast.success(editing ? "Responsável atualizado." : "Responsável cadastrado.");
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/guardians/${deleting.id}`);
      toast.success("Responsável excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDeleting(null);
    }
  };

  const openDetail = async (guardian) => {
    try {
      const { data } = await api.get(`/guardians/${guardian.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="guardians-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Responsáveis</h1>
          <p className="mt-1 text-sm text-muted-foreground">Responsáveis legais vinculados aos alunos.</p>
        </div>
        <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="guardian-add-button">
          <Plus size={16} className="mr-2" /> Novo responsável
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex flex-1 items-center gap-2"
        >
          <Input
            placeholder="Pesquisar por nome ou CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="guardian-search-input"
          />
          <Button type="submit" variant="outline" size="icon" data-testid="guardian-search-button">
            <Search size={16} />
          </Button>
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="guardian-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">CPF</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Contato</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Alunos</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {guardians.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="p-6 text-sm text-muted-foreground" data-testid="guardians-empty">
                  Nenhum responsável encontrado.
                </TableCell>
              </TableRow>
            )}
            {guardians.map((g) => (
              <TableRow key={g.id} className="transition-colors hover:bg-zinc-50" data-testid={`guardian-row-${g.id}`}>
                <TableCell className="font-medium">{g.full_name}</TableCell>
                <TableCell className="hidden md:table-cell">{fmtCPF(g.cpf)}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <p className="text-sm">{g.whatsapp}</p>
                  <p className="text-xs text-muted-foreground">{g.email}</p>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <Badge variant="secondary">{g.students_count}</Badge>
                </TableCell>
                <TableCell><StatusBadge status={g.status} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Visualizar" onClick={() => openDetail(g)} data-testid={`guardian-view-${g.id}`}>
                    <Eye size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openDialog(g)} data-testid={`guardian-edit-${g.id}`}>
                    <Pencil size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleting(g)} data-testid={`guardian-delete-${g.id}`}>
                    <Trash2 size={16} className="text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="guardian-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar responsável" : "Novo responsável"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="guardian-form-error">{error}</p>}
            <div className="space-y-2">
              <Label>Nome completo *</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="guardian-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>WhatsApp *</Label>
                <Input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="(11) 99999-9999" data-testid="guardian-whatsapp-input" />
              </div>
              <div className="space-y-2">
                <Label>E-mail *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="guardian-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>CPF (opcional)</Label>
                <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="guardian-cpf-input" />
              </div>
              <div className="space-y-2">
                <Label>Data de nascimento (opcional)</Label>
                <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="guardian-birthdate-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Endereço</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="guardian-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="guardian-notes-input" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="guardian-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="guardian-save-button">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="guardian-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.full_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</p><p className="font-medium">{detail.whatsapp}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">E-mail</p><p className="font-medium">{detail.email}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p><StatusBadge status={detail.status} /></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">CPF</p><p className="font-medium">{fmtCPF(detail.cpf)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Nascimento</p><p className="font-medium">{detail.birth_date ? fmtBR(detail.birth_date) : "—"}</p></div>
                <div className="col-span-2 sm:col-span-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Endereço</p><p className="font-medium">{detail.address || "—"}</p></div>
                {detail.notes && (
                  <div className="col-span-2 sm:col-span-3"><p className="text-xs uppercase tracking-wider text-muted-foreground">Observações</p><p className="font-medium">{detail.notes}</p></div>
                )}
              </div>

              <div>
                <h3 className="font-heading text-base font-semibold">Alunos vinculados</h3>
                <div className="mt-2 space-y-2" data-testid="guardian-students-list">
                  {detail.students.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="guardian-students-empty">
                      Nenhum aluno vinculado. Vincule pela página Alunos.
                    </p>
                  )}
                  {detail.students.map((s) => (
                    <div key={s.link_id} className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid={`guardian-student-${s.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">
                          {s.full_name}
                          {s.is_primary && (
                            <Badge className="ml-2 bg-primary text-primary-foreground">Principal</Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{RELATIONSHIP_LABELS[s.relationship]} · {s.age} anos</p>
                      </div>
                      <StatusBadge status={s.status} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Excluir responsável"
        description={`Deseja excluir "${deleting?.full_name}"? Os vínculos com alunos também serão removidos.`}
        onConfirm={remove}
        testId="guardian-delete-confirm"
      />
    </div>
  );
}
